import { useEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';
import { api } from '../lib/api';
import type { Banner, Category, Item } from '../lib/types';

type Tab = 'categories' | 'items' | 'banners';

function parseTags(tags: string | null): string[] {
  if (!tags) return [];
  try {
    const parsed = JSON.parse(tags);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('categories');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<{ email: string; role: 'owner' | 'admin' } | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newItem, setNewItem] = useState({
    category_id: 0,
    name: '',
    price: 0,
    description: '',
    sort_order: 0,
    image_url: '',
    is_active: 1,
    is_soldout: 0,
    visibility_qr: 1,
    visibility_tablet: 1,
    tags: '추천'
  });
  const [newBannerTitle, setNewBannerTitle] = useState('');

  async function loadAll() {
    try {
      setLoading(true);
      const [adminState, catRes, itemRes, bannerRes] = await Promise.all([
        api.getAdminState(),
        api.getCategories(),
        api.getItems(),
        api.getBanners()
      ]);
      setState(adminState);
      setCategories(catRes.categories);
      setItems(itemRes.items);
      setBanners(bannerRes.banners);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '관리자 권한이 필요합니다.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  const categoryMap = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);

  async function optimisticToggleItem(item: Item, key: 'is_active' | 'is_soldout' | 'visibility_qr' | 'visibility_tablet') {
    const prev = [...items];
    const toggled: Item = { ...item, [key]: item[key] === 1 ? 0 : 1 };
    setItems((current) => current.map((i) => (i.id === item.id ? toggled : i)));

    try {
      await api.updateItem(item.id, { ...toggled, tags: parseTags(toggled.tags) });
    } catch {
      setItems(prev);
      alert('저장 실패: 이전 상태로 복구했습니다.');
    }
  }

  if (loading) {
    return <p>관리자 페이지 로딩 중...</p>;
  }

  if (error) {
    return (
      <section className="stack-md">
        <Seo title="타로맨스 | 관리자" description="관리자 페이지" />
        <h1>관리자 접근 실패</h1>
        <pre className="error-box">{error}</pre>
      </section>
    );
  }

  return (
    <section className="admin-layout">
      <Seo title="타로맨스 | 관리자" description="메뉴/카테고리/배너 관리자" />
      <aside className="admin-side card">
        <h2>관리자</h2>
        <p>{state?.email}</p>
        <p className="muted">role: {state?.role}</p>
        <button className={tab === 'categories' ? 'btn active' : 'btn'} onClick={() => setTab('categories')}>카테고리</button>
        <button className={tab === 'items' ? 'btn active' : 'btn'} onClick={() => setTab('items')}>아이템</button>
        <button className={tab === 'banners' ? 'btn active' : 'btn'} onClick={() => setTab('banners')}>배너</button>
      </aside>

      <section className="card admin-main">
        {tab === 'categories' && (
          <div className="stack-md">
            <h3>카테고리 관리</h3>
            <div className="inline-form">
              <input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="카테고리 이름" />
              <button
                className="btn"
                onClick={async () => {
                  if (!newCategoryName.trim()) return;
                  await api.createCategory({ name: newCategoryName, sort_order: categories.length + 1, is_active: 1 });
                  setNewCategoryName('');
                  loadAll();
                }}
              >
                추가
              </button>
            </div>
            <div className="stack-sm">
              {categories.map((c) => (
                <div key={c.id} className="admin-row">
                  <input
                    value={c.name}
                    onChange={(e) => setCategories((list) => list.map((x) => (x.id === c.id ? { ...x, name: e.target.value } : x)))}
                  />
                  <input
                    type="number"
                    value={c.sort_order}
                    onChange={(e) =>
                      setCategories((list) =>
                        list.map((x) => (x.id === c.id ? { ...x, sort_order: Number(e.target.value) } : x))
                      )
                    }
                  />
                  <label><input type="checkbox" checked={c.is_active === 1} onChange={(e) => setCategories((list) => list.map((x) => (x.id === c.id ? { ...x, is_active: e.target.checked ? 1 : 0 } : x)))} />활성</label>
                  <button className="btn" onClick={() => api.updateCategory(c.id, c).then(loadAll)}>저장</button>
                  <button className="btn danger" onClick={() => api.deleteCategory(c.id).then(loadAll)}>삭제</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'items' && (
          <div className="stack-md">
            <h3>아이템 관리</h3>
            <div className="grid-two">
              <select value={newItem.category_id} onChange={(e) => setNewItem({ ...newItem, category_id: Number(e.target.value) })}>
                <option value={0}>카테고리 선택</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <input placeholder="이름" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
              <input type="number" placeholder="가격" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: Number(e.target.value) })} />
              <input placeholder="설명" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
              <input type="number" placeholder="정렬" value={newItem.sort_order} onChange={(e) => setNewItem({ ...newItem, sort_order: Number(e.target.value) })} />
              <input placeholder="이미지 URL" value={newItem.image_url} onChange={(e) => setNewItem({ ...newItem, image_url: e.target.value })} />
              <input placeholder="태그(쉼표)" value={newItem.tags} onChange={(e) => setNewItem({ ...newItem, tags: e.target.value })} />
              <button
                className="btn"
                onClick={async () => {
                  await api.createItem({
                    ...newItem,
                    tags: newItem.tags.split(',').map((v) => v.trim()).filter(Boolean)
                  });
                  setNewItem({
                    category_id: 0,
                    name: '',
                    price: 0,
                    description: '',
                    sort_order: 0,
                    image_url: '',
                    is_active: 1,
                    is_soldout: 0,
                    visibility_qr: 1,
                    visibility_tablet: 1,
                    tags: '추천'
                  });
                  loadAll();
                }}
              >
                아이템 추가
              </button>
            </div>

            <div className="stack-sm">
              {items.map((item) => (
                <div key={item.id} className="admin-row tall">
                  <div>
                    <strong>{item.name}</strong>
                    <p className="muted">{categoryMap.get(item.category_id)} | {item.price.toLocaleString()}원</p>
                  </div>
                  <div className="toggle-row">
                    <label><input type="checkbox" checked={item.is_active === 1} onChange={() => optimisticToggleItem(item, 'is_active')} />활성</label>
                    <label><input type="checkbox" checked={item.is_soldout === 1} onChange={() => optimisticToggleItem(item, 'is_soldout')} />품절</label>
                    <label><input type="checkbox" checked={item.visibility_qr === 1} onChange={() => optimisticToggleItem(item, 'visibility_qr')} />QR</label>
                    <label><input type="checkbox" checked={item.visibility_tablet === 1} onChange={() => optimisticToggleItem(item, 'visibility_tablet')} />태블릿</label>
                  </div>
                  <button className="btn danger" onClick={() => api.deleteItem(item.id).then(loadAll)}>삭제</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'banners' && (
          <div className="stack-md">
            <h3>배너 관리</h3>
            <div className="inline-form">
              <input value={newBannerTitle} onChange={(e) => setNewBannerTitle(e.target.value)} placeholder="배너 제목" />
              <button
                className="btn"
                onClick={async () => {
                  if (!newBannerTitle.trim()) return;
                  await api.createBanner({ title: newBannerTitle, is_active: 1, sort_order: banners.length + 1 });
                  setNewBannerTitle('');
                  loadAll();
                }}
              >
                추가
              </button>
            </div>

            {banners.map((banner) => (
              <div key={banner.id} className="admin-row">
                <input
                  value={banner.title}
                  onChange={(e) =>
                    setBanners((list) => list.map((x) => (x.id === banner.id ? { ...x, title: e.target.value } : x)))
                  }
                />
                <label>
                  <input
                    type="checkbox"
                    checked={banner.is_active === 1}
                    onChange={(e) =>
                      setBanners((list) =>
                        list.map((x) => (x.id === banner.id ? { ...x, is_active: e.target.checked ? 1 : 0 } : x))
                      )
                    }
                  />
                  활성
                </label>
                <button className="btn" onClick={() => api.updateBanner(banner.id, banner).then(loadAll)}>저장</button>
                <button className="btn danger" onClick={() => api.deleteBanner(banner.id).then(loadAll)}>삭제</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </section>
  );
}
